import type { ReactNode } from "react";

import { Button, type Requirement } from "./Button";
import { cx } from "./cx";

const SIZES = {
  lg: { track: "h-6 w-10", knob: "size-5", on: "left-[18px]" },
  md: { track: "h-[22px] w-[38px]", knob: "size-[18px]", on: "left-[18px]" },
  sm: { track: "h-5 w-[34px]", knob: "size-4", on: "left-4" },
} as const;

/**
 * An on/off switch. `tone="dark"` is the dark track of the General settings rows; `brand` is the coral one.
 * `requires` or `disabledReason` leave it focusable and say why it can't be changed ("Always on");
 * `busy` disables it while a change is saving.
 */
export function Toggle({
  checked,
  onChange,
  label,
  size = "sm",
  tone = "brand",
  busy = false,
  requires,
  disabledReason,
  dispute,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  /** The switch's accessible name (there is no visible text). */
  label: string;
  size?: keyof typeof SIZES;
  tone?: "brand" | "dark";
  busy?: boolean;
  /** Unavailable, saying which role to ask for, unless the person holds this permission. */
  requires?: Requirement | readonly Requirement[];
  disabledReason?: ReactNode;
  /** Unavailable while a dispute locks the domain (`DisputeBlocked`). */
  dispute?: boolean;
}) {
  const s = SIZES[size];
  return (
    <Button
      unstyled
      role="switch"
      aria-checked={checked}
      aria-label={label}
      loading={busy}
      requires={requires}
      disabledReason={disabledReason}
      dispute={dispute}
      onClick={() => onChange(!checked)}
      className={cx(
        "relative shrink-0 rounded-full transition-colors focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        s.track,
        checked ? (tone === "dark" ? "bg-[#221c19]" : "bg-brand-500") : "bg-[#d4cac4]",
      )}
    >
      <span
        aria-hidden
        className={cx(
          "absolute top-0.5 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.2)] transition-[left]",
          s.knob,
          checked ? s.on : "left-0.5",
        )}
      />
    </Button>
  );
}
